import { Bell, Edit2, Send, X, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";

export const NotifyPartiesDialog = ({
  open,
  onOpenChange,
  notifyData,
  setNotifyData,
  onSend,
  sending,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="notify-parties-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-[var(--t-primary)]" />
          Notify Parties — Deadline Update
        </DialogTitle>
        <DialogDescription>
          Review and send a deadline update notification to selected parties.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 mt-2">
        {/* Recipients */}
        <div>
          <Label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider block mb-1">Recipients</Label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {notifyData.recipients.map((email, i) => (
              <Badge key={i} variant="secondary" className="text-xs flex items-center gap-1">
                {email}
                <button
                  onClick={() => setNotifyData(d => ({ ...d, recipients: d.recipients.filter((_, j) => j !== i) }))}
                  className="hover:text-red-600"
                  data-testid={`remove-recipient-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Add email..."
              className="h-8 text-xs"
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.value.includes("@")) {
                  setNotifyData(d => ({ ...d, recipients: [...d.recipients, e.target.value.trim()] }));
                  e.target.value = "";
                }
              }}
              data-testid="add-recipient-input"
            />
          </div>
        </div>
        {/* Subject */}
        <div>
          <Label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider block mb-1">Subject</Label>
          <Input
            value={notifyData.subject}
            onChange={(e) => setNotifyData(d => ({ ...d, subject: e.target.value }))}
            className="h-8 text-sm"
            data-testid="notify-subject-input"
          />
        </div>
        {/* Body */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider">Email Body</Label>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => {
                if (!notifyData.editingBody) {
                  const tmp = document.createElement("div");
                  tmp.innerHTML = notifyData.body;
                  setNotifyData(d => ({ ...d, editingBody: true, bodyText: tmp.innerText || tmp.textContent || "" }));
                } else {
                  const html = notifyData.bodyText
                    .split("\n")
                    .map(line => line.trim() ? `<p style="margin:0 0 6px;font-size:14px;color:#1a1a1a;">${line}</p>` : "<br/>")
                    .join("");
                  setNotifyData(d => ({ ...d, editingBody: false, body: `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;line-height:1.6;">${html}<div style="margin-top:24px;padding-top:16px;border-top:1px solid #e2e8f0;"><p style="color:#64748b;font-size:12px;margin:0;">Sent from EasyOffice Transaction Manager</p></div></div>` }));
                }
              }}
              data-testid="toggle-body-edit"
            >
              <Edit2 className="w-3 h-3 mr-1" />
              {notifyData.editingBody ? "Preview" : "Edit"}
            </Button>
          </div>
          {notifyData.editingBody ? (
            <textarea
              value={notifyData.bodyText || ""}
              onChange={(e) => setNotifyData(d => ({ ...d, bodyText: e.target.value }))}
              className="w-full border border-[var(--t-border)] rounded-md p-3 bg-white text-sm min-h-[200px] max-h-[300px] resize-y focus:outline-none focus:ring-1 focus:ring-[var(--t-primary)]"
              data-testid="notify-body-textarea"
            />
          ) : (
            <div
              className="border border-[var(--t-border)] rounded-md p-4 bg-white text-sm max-h-60 overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: notifyData.body }}
              data-testid="notify-body-preview"
            />
          )}
        </div>
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button
          onClick={onSend}
          disabled={sending || !notifyData.recipients.length}
          className="bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]"
          data-testid="send-notify-btn"
        >
          {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Notification</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
